/**
 * Iris procedures — natural-language, multi-step SOPs that activate when a
 * customer intent matches and then guide the AI service agent step-by-step
 * (using tools) toward a terminal state. Modeled on Intercom Fin's "Procedures":
 * a trigger intent decides when the SOP applies, the instructions are the
 * step-by-step playbook, an optional identity-verification gate protects
 * sensitive actions, and the end state resolves the conversation or hands it off
 * to a human. Distinct from guidance (always-on behavioral rules) and knowledge
 * (facts): a procedure is a task the agent runs.
 *
 * Proxied through operations at `/api/messaging/iris/procedures` (operations is
 * the FE's auth boundary + RBAC gate; it forwards to ai-operations with the
 * shared service key). Powers Settings → Iris → Train.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';

const BASE = '/api/messaging/iris/procedures';

/** Max length of a procedure's step-by-step instructions (matches the backend validator). */
export const PROCEDURE_INSTRUCTIONS_MAX_LENGTH = 5000;
/** Max length of the trigger-intent description (matches the backend validator). */
export const PROCEDURE_TRIGGER_MAX_LENGTH = 500;
/** Max length of a procedure name (matches the backend validator). */
export const PROCEDURE_NAME_MAX_LENGTH = 120;

// Must match the backend ProcedureEndState enum (ai_ops schema) exactly.
export const PROCEDURE_END_STATES = ['RESOLVE', 'HANDOFF'] as const;
export type ProcedureEndState = (typeof PROCEDURE_END_STATES)[number];

export const PROCEDURE_END_STATE_LABELS: Record<ProcedureEndState, string> = {
  RESOLVE: 'Resolve the conversation',
  HANDOFF: 'Hand off to a human',
};

export interface Procedure {
  id: string;
  name: string;
  /** Natural-language description of when this procedure applies (the intent match). */
  triggerIntent: string;
  /** The step-by-step SOP the agent follows. */
  instructions: string;
  /**
   * Gate sensitive actions behind identity verification before running the
   * steps. Named `requiresVerification` to match the API/Prisma field — the
   * create/update DTOs are whitelisted (`forbidNonWhitelisted`), so any other
   * spelling is rejected with a 400 rather than ignored.
   */
  requiresVerification: boolean;
  /** Terminal state once the steps complete. */
  endState: ProcedureEndState;
  enabled: boolean;
  sortOrder: number;
}

export interface CreateProcedurePayload {
  name: string;
  triggerIntent: string;
  instructions: string;
  requiresVerification?: boolean;
  endState?: ProcedureEndState;
  enabled?: boolean;
}

export interface UpdateProcedurePayload {
  name?: string;
  triggerIntent?: string;
  instructions?: string;
  requiresVerification?: boolean;
  endState?: ProcedureEndState;
  enabled?: boolean;
}

export const irisProceduresKey = ['messaging', 'iris-procedures'] as const;

export function useProcedures() {
  return useQuery({
    queryKey: irisProceduresKey,
    queryFn: () => apiGet<Procedure[]>(operationsClient, BASE),
    staleTime: 30_000,
  });
}

export function useCreateProcedure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProcedurePayload) =>
      apiPost<Procedure, CreateProcedurePayload>(operationsClient, BASE, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: irisProceduresKey });
    },
  });
}

export function useUpdateProcedure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & UpdateProcedurePayload) =>
      apiPut<Procedure, UpdateProcedurePayload>(
        operationsClient,
        `${BASE}/${encodeURIComponent(id)}`,
        payload,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: irisProceduresKey });
    },
  });
}

export function useDeleteProcedure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete<void>(operationsClient, `${BASE}/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: irisProceduresKey });
    },
  });
}
